/**
 * A model of one's own, in the browser.
 * ---------------------------------------------------------------------------
 * Everything else in this product asks a server. This does not: the weights
 * are fetched once, kept in this browser, and the answering happens on the
 * machine in front of you - on its GPU, through WebGPU, with nothing leaving
 * it. That is the whole point of having it here rather than behind an API:
 * an estate's names, its addresses and its findings are the things you would
 * least like to post somewhere, and they are exactly what you want to ask
 * about.
 *
 * Two things make it practical:
 *
 *   WEBGPU, which is what makes a model this size answer in a sentence rather
 *   than in a minute. Chrome and Edge have it. Where it is missing there is
 *   nothing to fall back to that is worth having, so this says so plainly
 *   instead of running a model at a word a second.
 *
 *   THE CACHE. The weights are a few hundred megabytes; downloading them once
 *   per visit would make the feature unusable. WebLLM can keep them in the
 *   Cache API or in IndexedDB, and this asks for IndexedDB - it is the store
 *   the rest of this app already keeps its estate in, it survives a reload and
 *   a restart, and what is in it can be seen and cleared from one place.
 *
 * The library itself is loaded only when somebody asks for a model: it is
 * fourteen megabytes of JavaScript, and a page that nobody takes the butler to
 * should not pay for it.
 * ---------------------------------------------------------------------------
 */

/* What can be downloaded, smallest first. Sizes are what the weights actually
   cost over the wire, which is the number someone deciding cares about - not
   the VRAM figure the library reports. */
export const MODELS = [
  {
    id: "SmolLM2-360M-Instruct-q4f16_1-MLC",
    label: "SmolLM2 360M",
    size: "~270 MB",
    what: "The smallest that still holds a conversation. Quick on any machine; will lose the thread of a long one.",
  },
  {
    id: "Qwen2.5-0.5B-Instruct-q4f16_1-MLC",
    label: "Qwen2.5 0.5B",
    size: "~380 MB",
    what: "The one to start with: follows an instruction properly and still loads in under a minute on a decent line.",
    recommended: true,
  },
  {
    id: "Llama-3.2-1B-Instruct-q4f16_1-MLC",
    label: "Llama 3.2 1B",
    size: "~710 MB",
    what: "Twice the size and noticeably better at anything that needs reasoning rather than recall.",
  },
  {
    id: "gemma-2-2b-it-q4f16_1-MLC-1k",
    label: "Gemma 2 2B",
    size: "~1.5 GB",
    what: "The best of these by some way, and the slowest to fetch. Wants a machine with real GPU memory.",
  },
];

export const DEFAULT_MODEL = MODELS.find(m => m.recommended).id;

/* Where the last choice is remembered - a preference about this machine, like
   the renderer and the palettes. */
const CHOICE_KEY = "butler.model";

export const loadChoice = () => {
  try { return localStorage.getItem(CHOICE_KEY) || DEFAULT_MODEL; }
  catch (e) { return DEFAULT_MODEL; }
};

export const saveChoice = (id) => {
  try { localStorage.setItem(CHOICE_KEY, id); } catch (e) { /* private window */ }
};

/** Can this browser run one at all? */
export const canRun = () => typeof navigator !== "undefined" && !!navigator.gpu;

/* The library, fetched once and kept. */
let lib = null;
const library = async () => {
  if (!lib) lib = await import("@mlc-ai/web-llm");
  return lib;
};

/* The engine, which is a model held open. One of them, because two would be
   two copies of the weights in GPU memory. */
let engine = null;
let loaded = null;

/* ...and the cache told to live in IndexedDB rather than in the Cache API,
   which is the one thing this module insists on.

   The option is `cacheBackend: "indexeddb"`. Older WebLLM took a boolean named
   `useIndexedDBCache`, and that is worth saying here because an unknown key on
   this object is ignored in silence: set the old one against a current build
   and everything works, the weights land in the Cache API, and nothing
   anywhere says so. */
const configFor = async () => {
  const webllm = await library();
  return { ...webllm.prebuiltAppConfig, cacheBackend: "indexeddb" };
};

/** Is this model already on the machine? */
export async function isCached(id) {
  try {
    const webllm = await library();
    return await webllm.hasModelInCache(id, await configFor());
  } catch (e) {
    return false;
  }
}

/** Which model the engine currently holds, if any. */
export const held = () => loaded;

/**
 * Bring up the last model used, but only if it is already on the machine.
 *
 * What makes this safe to run on every page load: it never downloads. A model
 * that is here costs a second of GPU setup and is then ready for whatever gets
 * asked next; one that is not stays not here until somebody presses the button
 * that says how many megabytes it is.
 */
export async function wakeIfCached(onStep) {
  if (!canRun()) return null;
  const id = loadChoice();
  if (loaded === id) return id;
  try {
    if (!(await isCached(id))) return null;
    await bring(id, onStep);
    return id;
  } catch (e) {
    return null;
  }
}

/**
 * Bring a model up: fetched if it is not here, read out of IndexedDB if it is.
 *
 * @param id       which one
 * @param onStep   called with { text, at } as it goes - `at` is 0..1 where the
 *                 library gives a figure, and the text is its own words, which
 *                 are better than anything this could invent ("fetching param
 *                 cache[12/38]...")
 */
export async function bring(id, onStep) {
  if (!canRun()) throw new Error("This browser has no WebGPU, so there is nothing to run a model on. Chrome and Edge have it.");
  if (engine && loaded === id) return engine;

  /* a different model: let the old one go first, or the machine holds two */
  if (engine) await release();

  const webllm = await library();
  engine = await webllm.CreateMLCEngine(id, {
    appConfig: await configFor(),
    initProgressCallback: (r) => {
      if (!onStep) return;
      onStep({ text: r?.text || "", at: typeof r?.progress === "number" ? r.progress : null });
    },
  });
  loaded = id;
  return engine;
}

/** Let go of the model and the memory it is holding. */
export async function release() {
  const was = engine;
  engine = null;
  loaded = null;
  try { await was?.unload?.(); } catch (e) { /* already gone */ }
}

/** Take a model off the machine again. */
export async function forget(id) {
  await release();
  const webllm = await library();
  await webllm.deleteModelAllInfoInCache(id, await configFor());
}

/**
 * Ask it something.
 *
 * Streamed, because a model this size writes at about reading speed and
 * watching it arrive is the difference between "thinking" and "hung".
 *
 * @param messages  the conversation so far, OpenAI's shape
 * @param onWord    called with the whole answer so far, each time it grows
 */
export async function ask(messages, onWord, { temperature = 0.4, signal, max = 180 } = {}) {
  if (!engine) throw new Error("No model is loaded.");

  const stream = await engine.chat.completions.create({
    messages,
    temperature,
    /* A model this small will say the same true thing forever if nothing stops
       it: asked where machines are, the smallest of them named Discovery and
       then listed it fourteen more times. The ceiling ends a loop, and the
       penalty makes one much less likely to start - both are cheap, and
       neither costs a short answer anything, which is the only kind this page
       asks for. */
    max_tokens: max,
    frequency_penalty: 0.6,
    presence_penalty: 0.3,
    stream: true,
  });

  let said = "";
  for await (const chunk of stream) {
    if (signal?.aborted) break;
    said += chunk?.choices?.[0]?.delta?.content || "";
    if (onWord) onWord(said);
  }
  return said;
}

/**
 * Ask for an answer of a given SHAPE, rather than for prose.
 * ---------------------------------------------------------------------------
 * This is what makes a model of this size usable for deciding something. Given
 * a JSON schema, WebLLM constrains what the model is allowed to emit token by
 * token - so the answer is not "usually valid JSON with the right keys", it is
 * JSON matching that schema, every time. An enum of page ids in the schema is
 * therefore a model that cannot invent a page.
 *
 * Not streamed: nobody reads JSON as it arrives.
 */
export async function decide(messages, schema, { temperature = 0.2, max = 160 } = {}) {
  if (!engine) throw new Error("No model is loaded.");

  const reply = await engine.chat.completions.create({
    messages,
    temperature,
    max_tokens: max,
    response_format: { type: "json_object", schema: JSON.stringify(schema) },
  });

  const text = reply?.choices?.[0]?.message?.content || "";
  try {
    return JSON.parse(text);
  } catch (e) {
    /* constrained or not, a model can still stop early: take the first object
       out of whatever came back rather than failing on the whole */
    const at = text.indexOf("{");
    const to = text.lastIndexOf("}");
    if (at >= 0 && to > at) {
      try { return JSON.parse(text.slice(at, to + 1)); } catch (e2) { /* no */ }
    }
    return null;
  }
}

export default { MODELS, DEFAULT_MODEL, canRun, isCached, bring, ask, decide, release, forget, wakeIfCached };
